import React, {useState, useMemo, useEffect} from "react";
import ReportItem from "./ReportItem/ReportItem";
import {
  Paper,
  Container,
  Grid,
  Skeleton,
  LinearProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Chip,
  Divider,
} from "@mui/material";
import { Search, FilterList } from "@mui/icons-material";
import {DimensionamentoService} from "../../services/DimensionamentoServices";

const ReportTable = ({ navigation, selectedStep }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  const isLoading = !navigation || navigation.length === 0;
  const [hasRootCause, setHasRootCause] = useState(null);

  const filteredReports = useMemo(() => {
    if (isLoading) return [];

    const allIndividualReports = [];
    navigation.forEach((step) => {
      if (step.direct) {
        const reports = Array.isArray(step.direct)
          ? step.direct
          : [step.direct];
        reports.forEach((direct) => {
          allIndividualReports.push({
            step,
            direct,
            isAvailable: step.completed,
            isUnlocked: step.unlocked,
          });
        });
      }
    });

    let filtered = allIndividualReports;

    const causeAndActionReport = filtered.find(
      (report) => report.direct.customName === "Gestão de Rede"
    );

    causeAndActionReport.isAvailable = hasRootCause;
    causeAndActionReport.isUnlocked = hasRootCause;

    if (filter === "available") {
      filtered = filtered.filter((report) => report.isAvailable);
    } else if (filter === "blocked") {
      filtered = filtered.filter((report) => !report.isAvailable);
    }

    if (searchTerm) {
      filtered = filtered.filter((report) =>
        (report.direct.customName || report.direct.name)
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [navigation, filter, searchTerm, isLoading, hasRootCause]);

  const getAllIndividualReports = (nav) => {
    const allReports = [];
    nav.forEach((step) => {
      if (step.direct) {
        const reports = Array.isArray(step.direct)
          ? step.direct
          : [step.direct];
        reports.forEach((direct) => {
          allReports.push({
            ...direct,
            stepCompleted: step.completed,
            stepUnlocked: step.unlocked,
          });
        });
      }
    });
    return allReports;
  };

  const allIndividualReports = getAllIndividualReports(navigation);
  const availableIndividualReports = allIndividualReports.filter(
    (report) => report.stepCompleted
  );
  const progressPercentage =
    allIndividualReports.length > 0
      ? (availableIndividualReports.length / allIndividualReports.length) * 100
      : 0;

  useEffect(() => {
    const fetchRootCauseStatus = async () => {
      try {
        const response = await DimensionamentoService.hasAnyCauseAndActionReport()
        setHasRootCause(response);
      } catch (error) {
        console.error("Erro ao obter estado de causa raiz:", error);
        setHasRootCause(false);
      }
    }

    fetchRootCauseStatus();
  }, []);

  return (
    <Container maxWidth="xl" className="py-8">
      <Fade in={true} timeout={800}>
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Central de <span className="text-emerald-700">Relatórios</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Acesse e visualize todos os relatórios disponíveis do sistema de
            dimensionamento
          </p>
          {!isLoading && allIndividualReports.length > 0 && (
            <div className="max-w-md mx-auto mb-8">
              <div className="flex justify-between items-center mb-2">
                <Typography variant="body2" className="text-gray-600">
                  Progresso dos Relatórios
                </Typography>
                <Chip
                  label={`${availableIndividualReports.length} de ${allIndividualReports.length}`}
                  size="small"
                  color="primary"
                  sx={{
                    backgroundColor: "#059669",
                    "& .MuiChip-label": { fontWeight: 600 },
                  }}
                />
              </div>
              <Box className="relative">
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  className="h-3 rounded-full bg-gray-200"
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#059669",
                      borderRadius: "9999px",
                      backgroundImage:
                        "linear-gradient(90deg, rgba(5,150,105,1) 0%, rgba(16,185,129,1) 100%)",
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                className="text-gray-500 mt-2 block"
              >
                {Math.round(progressPercentage)}% dos relatórios disponíveis
              </Typography>
            </div>
          )}
        </div>
      </Fade>
      {!isLoading && allIndividualReports.length > 0 && (
        <Fade in={true} timeout={1200}>
          <div className="mb-8">
            <div className="flex flex-col xl:flex-row gap-4 items-center justify-between mb-6">
              <div className="w-full xl:w-auto">
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-800 mb-2 md:mb-0"
                >
                  Todos os Relatórios
                  {filteredReports.length > 0 && (
                    <Chip
                      label={`${filteredReports.length} ${
                        filteredReports.length === 1
                          ? "encontrado"
                          : "encontrados"
                      }`}
                      size="small"
                      className="ml-2 bg-gray-100"
                    />
                  )}
                </Typography>
              </div>

              <div className="flex flex-col xl:flex-row gap-4 items-center w-full xl:w-auto">
                <TextField
                  placeholder="Buscar relatórios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full xl:w-96"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      "&:hover fieldset": {
                        borderColor: "#059669",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#059669",
                      },
                    },
                  }}
                />

                <div className="flex gap-2">
                  <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(e, newFilter) =>
                      newFilter && setFilter(newFilter)
                    }
                    className="border border-gray-300 rounded-lg"
                  >
                    <ToggleButton
                      value="all"
                      className="px-4 py-2 text-sm"
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#059669",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#047857",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                        },
                      }}
                    >
                      <FilterList className="mr-2 h-4 w-4" />
                      Todos
                    </ToggleButton>
                    <ToggleButton
                      value="available"
                      className="px-4 py-2 text-sm"
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#059669",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#047857",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                        },
                      }}
                    >
                      Disponíveis
                    </ToggleButton>
                    <ToggleButton
                      value="blocked"
                      className="px-4 py-2 text-sm"
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#059669",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#047857",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                        },
                      }}
                    >
                      Bloqueados
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>
            </div>

            <Divider className="mb-6" />
          </div>
        </Fade>
      )}

      {isLoading ? (
        <Grid container spacing={4} justifyContent="center">
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={item}>
              <Fade in={true} timeout={800 + item * 100}>
                <Paper elevation={1} className="p-6 h-64">
                  <div className="flex items-start justify-between mb-4">
                    <Skeleton variant="circular" width={48} height={48} />
                    <Skeleton variant="rounded" width={80} height={24} />
                  </div>
                  <Skeleton variant="text" height={32} className="mb-3" />
                  <Skeleton variant="text" height={20} className="mb-2" />
                  <Skeleton variant="text" height={20} className="mb-4" />
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Skeleton variant="text" width={120} height={20} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </div>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid
          container
          spacing={4}
          justifyContent="center"
          sx={{ alignItems: "stretch" }}
        >
          {filteredReports.map((report, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              lg={4}
              xl={3}
              key={`report-${index}`}
              sx={{ display: "flex" }}
            >
              <Fade
                in={true}
                timeout={800 + index * 50}
                style={{ width: "100%", display: "flex" }}
              >
                <Box sx={{ width: "100%", display: "flex" }}>
                  <ReportItem
                    direct={report.direct}
                    selectedStep={selectedStep}
                    available={report.isAvailable}
                    unlocked={report.isUnlocked}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  />
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading &&
        filteredReports.length === 0 &&
        allIndividualReports.length > 0 && (
          <Fade in={true} timeout={800}>
            <Paper
              elevation={1}
              className="p-8 text-center bg-gray-50 rounded-xl"
            >
              <div className="text-gray-500">
                <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhum relatório encontrado
                </h3>
                <p className="text-gray-500">
                  Tente ajustar sua busca ou filtros
                </p>
              </div>
            </Paper>
          </Fade>
        )}
      {!isLoading && allIndividualReports.length === 0 && (
        <Fade in={true} timeout={800}>
          <Paper
            elevation={2}
            className="p-12 text-center bg-gray-50 rounded-xl"
          >
            <div className="text-gray-500">
              <svg
                className="mx-auto h-16 w-16 text-gray-400 mb-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                Nenhum relatório disponível
              </h3>
              <p className="text-gray-500 text-lg">
                Complete as etapas necessárias para gerar relatórios
              </p>
            </div>
          </Paper>
        </Fade>
      )}
    </Container>
  );
};

export default ReportTable;
